import { useCallback, useRef, useState } from 'react';
import { Trans } from 'react-i18next';

import { Alert } from '@zooplus/ui-components';

import AdyenCreditCardForm, {
  AdyenCreditCardFormRef,
} from '@components/AdyenCreditCardForm/AdyenCreditCardForm';
import { useTranslation } from '@hooks/useTranslations';
import { useConfig } from '@providers/ConfigProvider';
import CoreProviders, { CoreProvidersProps } from '@providers/CoreProviders';
import { useErrorContext } from '@providers/ErrorContextProvider';
import { captureException } from '@services/sentryService';
import { CreditCardBrand, CustomerType } from '@types-internal';
import { AdyenEnvironment } from '@types-internal/AdyenTypes';

export interface AdyenCaptureCreditCardsProps extends CoreProvidersProps {
  customerType: CustomerType;
  currency: string;
  isFallbackPsp: boolean;
  adyenEnvironment: AdyenEnvironment;
  supportedCreditCardBrands: CreditCardBrand[];
}

interface InnerAdyenCaptureCreditCardsProps {
  customerType: CustomerType;
  isFallbackPsp: boolean;
  adyenEnvironment: AdyenEnvironment;
  supportedCreditCardBrands: CreditCardBrand[];
}

/**
 * Component to solely capture the credit card information with Adyen.
 * Does not provide a mean to save it: external component must use the globally-exposed 'computeAdyenCreditCardFormData'.
 *
 * To be used on the registration page.
 */
const InnerAdyenCaptureCreditCards: React.FC<InnerAdyenCaptureCreditCardsProps> = ({
  customerType,
  isFallbackPsp,
  adyenEnvironment,
  supportedCreditCardBrands,
}) => {
  const form = useRef<AdyenCreditCardFormRef>(null);
  const { config } = useConfig();
  const { t } = useTranslation();
  const { setError } = useErrorContext();
  const [showForm, setShowForm] = useState(true);

  const onFatalError = useCallback(
    (error: unknown, showForm?: boolean) => {
      captureException({ error });
      setError({
        message: t('checkout.payment.creditcard.error.addcard.apicall'),
      });
      setShowForm(typeof showForm === 'undefined' ? true : showForm);
    },
    [setError],
  );

  return showForm ? (
    <>
      <Alert>
        <Trans
          t={t}
          i18nKey="checkout.payment.creditcard.3d.secure.message"
          components={{ url: <a target="_blank" href="/info/about/psd2_summary" /> }}
        />
      </Alert>
      <AdyenCreditCardForm
        ref={form}
        adyenEnvironment={adyenEnvironment}
        clientKey={config.adyen.clientKey}
        supportedCreditCardBrands={supportedCreditCardBrands}
        onFatalError={onFatalError}
        allowVaulting={!isFallbackPsp && customerType !== 'GUEST_CUSTOMER'}
        labelInline={false}
      />
    </>
  ) : null;
};

/*
 * Wrapper over the true component, because we first need to call the CoreProviders.
 */
const AdyenCaptureCreditCards: React.FC<AdyenCaptureCreditCardsProps> = ({
  env,
  shopBundle,
  locale,
  domain,
  errorContainerId,
  csrfToken,
  customerType,
  isFallbackPsp,
  adyenEnvironment,
  supportedCreditCardBrands,
}) => {
  return (
    <CoreProviders
      env={env}
      shopBundle={shopBundle}
      locale={locale}
      domain={domain}
      errorContainerId={errorContainerId}
      csrfToken={csrfToken}
      featureName="adyen-credit-card">
      <InnerAdyenCaptureCreditCards
        adyenEnvironment={adyenEnvironment}
        supportedCreditCardBrands={supportedCreditCardBrands}
        isFallbackPsp={isFallbackPsp}
        customerType={customerType}
      />
    </CoreProviders>
  );
};

export default AdyenCaptureCreditCards;
